/**
 * CommandPalette Component
 *
 * @module command-palette/components
 * @version 1.2
 * @updated 2026-04-27
 *
 * @description
 * Overlay de búsqueda global tipo "command palette".
 * Reutiliza useSearch, SearchApiService y MapNavigatorService existentes.
 * Siempre montado en el árbol (vía TopBar); renderiza null cuando está cerrado.
 *
 * @remarks
 * **Fase 2C**: Acciones rápidas locales integradas.
 * - Gestionar datos, Chat IA, cambio de módulo y modo operativo.
 * - Navegación por teclado unificada: acciones + resultados de búsqueda.
 * - Sin llamadas a backend para acciones locales.
 *
 * @pending
 * - Absorción de AddressSearch — Fase 2D
 * - Acciones de módulo dinámicas (exportar, análisis, etc.) — Fase 2E
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSearch } from '../../search/hooks/useSearch';
import { useProject } from '../../projects';
import { useCommandPaletteStore } from '../hooks/useCommandPalette';
import { useEditLock } from '../../layouts/EditLockContext';
import { useModuleStore } from '../../module-store';
import { buildDefaultActions, filterActions, groupActions } from '../actions/defaultActions';
import type { CommandAction, CommandActionGroup } from '../types';
import { COMMAND_GROUP_ORDER, COMMAND_GROUP_LABELS } from '../types';
import type { SearchHit } from '../../search/types';
import '../../search/components/search-results.css';
import './command-palette.css';

/**
 * Detecta macOS para mostrar hints de teclado correctos.
 * Evaluado una vez al cargar el módulo.
 */
const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.userAgent);

// ─── Search result icon helpers ───────────────────────────────────────────────
// Mismos iconos que SearchResults. TODO: extraer a shared util en Fase 2C.

function IconCoordinate(): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
    </svg>
  );
}

function IconEntity(): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
    </svg>
  );
}

function IconAddress(): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
    </svg>
  );
}

function IconNetwork(): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
    </svg>
  );
}

function IconSearch(): JSX.Element {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
  );
}

function getIconForKind(kind: string): JSX.Element {
  switch (kind) {
    case 'coordinate':   return <IconCoordinate />;
    case 'entity':       return <IconEntity />;
    case 'address':      return <IconAddress />;
    case 'network-node':
    case 'network-edge': return <IconNetwork />;
    default:             return <IconSearch />;
  }
}

// ─── Component props ──────────────────────────────────────────────────────────

export interface CommandPaletteProps {
  /** Abre el DataHubModal — callback desde TopBar */
  onOpenDataHub?: () => void;
  /** Abre/cierra el ChatPanel — callback desde TopBar */
  onToggleChat?: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * CommandPalette
 *
 * Montado en TopBar. Administra su propio estado de búsqueda y navegación.
 * Lee isOpen desde useCommandPaletteStore (Zustand).
 */
export function CommandPalette({ onOpenDataHub, onToggleChat }: CommandPaletteProps): JSX.Element | null {

  // ── Stores / context ──────────────────────────────────────────────────────
  const { isOpen, close } = useCommandPaletteStore();
  const { projectId } = useProject();
  const {
    query,
    results,
    totalFound,
    isLoading,
    error,
    search,
    navigateToHit,
    clearResults,
    setQuery,
  } = useSearch();
  const { isEditingActive, hasUnsavedChanges } = useEditLock();
  const { availableModules, activeModule } = useModuleStore();

  // ── Local state ───────────────────────────────────────────────────────────
  const [activeIndex, setActiveIndex] = useState(-1);

  // ── Refs ──────────────────────────────────────────────────────────────────
  const inputRef   = useRef<HTMLInputElement>(null);
  const modalRef   = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  /** Elemento que tenía foco antes de abrir — para restaurar al cerrar. */
  const triggerRef = useRef<Element | null>(null);
  const listboxId  = 'cp-results-listbox';

  // ── Actions (memoized) ────────────────────────────────────────────────────

  const allActions = useMemo(
    () => buildDefaultActions({ isEditingActive, hasUnsavedChanges, availableModules, activeModule, onOpenDataHub, onToggleChat }),
    [isEditingActive, hasUnsavedChanges, availableModules, activeModule, onOpenDataHub, onToggleChat]
  );

  const filteredActions = useMemo(
    () => filterActions(allActions, query),
    [allActions, query]
  );

  const groupedActions = useMemo(
    () => groupActions(filteredActions),
    [filteredActions]
  );

  /**
   * Offsets de índice global para cada grupo de acciones.
   * Permite mapear (group, localIndex) → globalIndex.
   */
  const groupOffsets = useMemo((): Record<CommandActionGroup, number> => {
    let offset = 0;
    const offsets = {} as Record<CommandActionGroup, number>;
    for (const group of COMMAND_GROUP_ORDER) {
      offsets[group] = offset;
      offset += (groupedActions.get(group)?.length ?? 0);
    }
    return offsets;
  }, [groupedActions]);

  /** Total de ítems navegables: acciones + resultados */
  const totalItems = filteredActions.length + results.length;

  // ── Effects ───────────────────────────────────────────────────────────────

  // Reset estado e historial de foco al abrir/cerrar
  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement;
      clearResults();
      setQuery('');
      setActiveIndex(-1);
      buttonRefs.current = [];
    } else {
      buttonRefs.current = [];
      if (triggerRef.current instanceof HTMLElement) {
        triggerRef.current.focus();
      }
      triggerRef.current = null;
    }
    // clearResults y setQuery son refs estables
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // AutoFocus input al abrir
  useEffect(() => {
    if (!isOpen) return;
    const id = setTimeout(() => { inputRef.current?.focus(); }, 50);
    return () => clearTimeout(id);
  }, [isOpen]);

  // Reset índice activo y limpiar refs stale cuando cambian acciones o resultados
  useEffect(() => {
    setActiveIndex(-1);
    buttonRefs.current = [];
  }, [filteredActions, results]);

  // ScrollIntoView para ítem activo por teclado
  useEffect(() => {
    if (activeIndex >= 0 && buttonRefs.current[activeIndex]) {
      buttonRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  // Búsqueda debounced: dispara API a los 250ms (solo si query ≥ 2)
  useEffect(() => {
    if (!isOpen) return;
    if (query.trim().length < 2) {
      clearResults();
      return;
    }
    const id = setTimeout(() => {
      if (projectId) void search(query, projectId);
    }, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, isOpen, projectId]);

  // Atajo global Cmd+K / Ctrl+K — abre/cierra la paleta
  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key === 'k') {
        e.preventDefault();
        useCommandPaletteStore.getState().toggle();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Focus trap: Tab / Shift+Tab cicla dentro del modal
  useEffect(() => {
    if (!isOpen) return;
    const handleTrapTab = (e: globalThis.KeyboardEvent): void => {
      if (e.key !== 'Tab' || !modalRef.current) return;
      const focusable = Array.from(
        modalRef.current.querySelectorAll<HTMLElement>(
          'input, button, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(el => !el.hasAttribute('disabled'));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', handleTrapTab);
    return () => document.removeEventListener('keydown', handleTrapTab);
  }, [isOpen]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleClose = useCallback((): void => {
    close();
    clearResults();
    setQuery('');
    setActiveIndex(-1);
  }, [close, clearResults, setQuery, setActiveIndex]);

  /** Ejecuta una acción rápida: cierra la paleta y llama onExecute. */
  const handleActionExecute = useCallback((action: CommandAction): void => {
    if (action.disabled) return;
    handleClose();
    action.onExecute();
  }, [handleClose]);

  const handleResultSelect = useCallback(async (hit: SearchHit): Promise<void> => {
    await navigateToHit(hit, 'select');
    handleClose();
  }, [navigateToHit, handleClose]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    setQuery(e.target.value);
  }, [setQuery]);

  const handleClear = useCallback((): void => {
    clearResults();
    setQuery('');
    setActiveIndex(-1);
    inputRef.current?.focus();
  }, [clearResults, setQuery]);

  /** Navegación y selección por teclado — unificada para acciones + resultados. */
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>): void => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (totalItems > 0) setActiveIndex(prev => Math.min(prev + 1, totalItems - 1));
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (totalItems > 0) setActiveIndex(prev => Math.max(prev - 1, -1));
        break;

      case 'Enter': {
        e.preventDefault();
        if (activeIndex < 0) break;
        if (activeIndex < filteredActions.length) {
          // Ítem activo es una acción rápida
          const action = filteredActions[activeIndex];
          if (action && !action.disabled) {
            handleClose();
            action.onExecute();
          }
        } else {
          // Ítem activo es un resultado de búsqueda
          const hit = results[activeIndex - filteredActions.length];
          if (hit) void handleResultSelect(hit);
        }
        break;
      }

      case 'Escape':
        e.preventDefault();
        handleClose();
        break;
    }
  }, [totalItems, activeIndex, filteredActions, results, handleClose, handleResultSelect]);

  /**
   * Cierra al hacer click en el backdrop (fuera del modal).
   * La comprobación target === currentTarget evita cerrar al hacer click dentro del modal.
   */
  const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget) handleClose();
  }, [handleClose]);

  // ── No renderizar cuando cerrado ──────────────────────────────────────────
  if (!isOpen) return null;

  // Derived display flags
  const queryIsActive = query.trim().length >= 2;
  const hasResults    = !error && results.length > 0 && queryIsActive;
  const showEmpty     = !isLoading && !error && queryIsActive && results.length === 0;
  const showHint      = query.trim().length < 2 && filteredActions.length === 0;
  const hasActions    = filteredActions.length > 0;

  // ── Portal ────────────────────────────────────────────────────────────────
  return createPortal(
    <div
      className="cp-backdrop"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="cp-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Buscar en WESTEER"
      >

        {/* ── Input row ── */}
        <div className="cp-input-row">
          <svg
            className="cp-search-icon"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>

          <input
            ref={inputRef}
            className="cp-input"
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Buscar o ejecutar una acción…"
            autoComplete="off"
            spellCheck={false}
            role="combobox"
            aria-expanded={hasResults || hasActions}
            aria-autocomplete="list"
            aria-controls={listboxId}
            aria-activedescendant={activeIndex >= 0 ? `cp-item-${activeIndex}` : undefined}
            disabled={!projectId}
          />

          {isLoading && (
            <div className="cp-spinner" aria-label="Buscando...">
              <svg
                className="cp-spinner-icon"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="cp-spinner-track" />
                <path
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          )}

          {query && !isLoading && (
            <button
              type="button"
              className="cp-clear-btn"
              onClick={handleClear}
              aria-label="Limpiar búsqueda"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}

          <kbd className="cp-kbd" title="Escape para cerrar">Esc</kbd>
        </div>

        {/* ── Divider ── */}
        <div className="cp-divider" />

        {/* ── Body ── */}
        <div className="cp-body" id={listboxId} role="listbox" aria-label="Resultados y acciones">

          {/* Estado vacío inicial — sin query y sin acciones */}
          {showHint && (
            <div className="cp-hint">
              <p>Escribí para buscar o ejecutar una acción</p>
            </div>
          )}

          {/* Acciones rápidas — visibles con cualquier query (filtradas) */}
          {hasActions && COMMAND_GROUP_ORDER.map(group => {
            const items = groupedActions.get(group) ?? [];
            if (items.length === 0) return null;
            const baseIdx = groupOffsets[group];
            return (
              <div key={group}>
                <div className="cp-group-header">{COMMAND_GROUP_LABELS[group]}</div>
                <ul className="cp-results-list" aria-label={COMMAND_GROUP_LABELS[group]}>
                  {items.map((action, i) => {
                    const idx = baseIdx + i;
                    const isActive = activeIndex === idx;
                    return (
                      <li key={action.id} role="presentation">
                        <button
                          id={`cp-item-${idx}`}
                          ref={(el) => { buttonRefs.current[idx] = el; }}
                          className={`search-result-button${isActive ? ' active' : ''}${action.disabled ? ' cp-action-disabled' : ''}`}
                          type="button"
                          role="option"
                          aria-selected={isActive}
                          aria-disabled={action.disabled}
                          onClick={() => handleActionExecute(action)}
                          title={action.disabled ? action.disabledReason : undefined}
                        >
                          <div className="result-icon">{action.icon}</div>
                          <div className="result-content">
                            <div className="result-label">{action.label}</div>
                            {action.description && (
                              <div className="result-sublabel">{action.description}</div>
                            )}
                          </div>
                          {action.disabled && (
                            <svg className="cp-action-lock-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                            </svg>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}

          {/* Separador entre acciones y resultados de búsqueda */}
          {hasActions && (hasResults || isLoading && queryIsActive) && (
            <div className="cp-divider" style={{ margin: '4px 0' }} />
          )}

          {/* Loading — solo si el query es activo (≥2 chars) */}
          {isLoading && queryIsActive && (
            <div className="cp-status">Buscando…</div>
          )}

          {/* Error */}
          {error && !isLoading && (
            <div className="cp-error">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* Sin resultados de búsqueda */}
          {showEmpty && (
            <div className={hasActions ? 'cp-status' : 'cp-empty'}>
              Sin resultados para &ldquo;{query}&rdquo;
            </div>
          )}

          {/* Resultados de búsqueda */}
          {hasResults && (
            <>
              {hasActions && (
                <div className="cp-group-header">Resultados</div>
              )}
              {totalFound > results.length && (
                <div className="cp-results-count">
                  Mostrando {results.length} de {totalFound}
                </div>
              )}
              <ul
                className="cp-results-list"
                aria-label="Resultados de búsqueda"
              >
                {results.map((hit, index) => {
                  const idx = filteredActions.length + index;
                  return (
                    <li key={index} role="presentation">
                      <button
                        id={`cp-item-${idx}`}
                        ref={(el) => { buttonRefs.current[idx] = el; }}
                        className={`search-result-button${activeIndex === idx ? ' active' : ''}`}
                        type="button"
                        role="option"
                        aria-selected={activeIndex === idx}
                        onClick={() => void handleResultSelect(hit)}
                      >
                        <div className="result-icon">
                          {getIconForKind(hit.kind)}
                        </div>
                        <div className="result-content">
                          <div className="result-label">{hit.label}</div>
                          {hit.sublabel && (
                            <div className="result-sublabel">{hit.sublabel}</div>
                          )}
                        </div>
                        {hit.score >= 90 ? (
                          <div className="result-score result-score-high">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                            </svg>
                          </div>
                        ) : hit.score >= 70 ? (
                          <div className="result-score result-score-medium">
                            {Math.round(hit.score)}
                          </div>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="cp-footer">
          <span className="cp-footer-hint">
            <kbd>↑↓</kbd>&nbsp;navegar
            &nbsp;&nbsp;
            <kbd>↵</kbd>&nbsp;ejecutar
            &nbsp;&nbsp;
            <kbd>Esc</kbd>&nbsp;cerrar
          </span>
          <span className="cp-footer-trigger">
            {isMac ? '⌘K' : 'Ctrl K'}
          </span>
        </div>

      </div>
    </div>,
    document.body
  );
}
